/**
 * Graph Viewer Page
 *
 * Interactive view of a knowledge graph with editing capabilities
 */
import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import GraphVisualization from '@/components/graphs/GraphVisualization'
import NodeEditor from '@/components/graphs/NodeEditor'
import RelationshipEditor from '@/components/graphs/RelationshipEditor'
import GraphSearch from '@/components/graphs/GraphSearch'
import { GraphNode } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'

const GraphViewerPage: React.FC = observer(() => {
  const navigate = useNavigate()
  const { graphId } = useParams<{ graphId: string }>()

  const [showNodeEditor, setShowNodeEditor] = useState(false)
  const [showRelationshipEditor, setShowRelationshipEditor] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  const [editingNode, setEditingNode] = useState<GraphNode | null>(null)

  const { selectedGraph, statistics, nodes } = knowledgeGraphStore

  useEffect(() => {
    if (graphId) {
      knowledgeGraphStore.fetchGraphById(graphId)
    }
  }, [graphId])

  const handleBack = () => {
    navigate('/knowledge-graphs')
  }

  const handleEditGraph = () => {
    navigate(`/knowledge-graphs/${graphId}/edit`)
  }

  const handleCreateNode = () => {
    setEditingNode(null)
    setShowNodeEditor(true)
  }

  const handleCreateRelationship = () => {
    setShowRelationshipEditor(true)
  }

  const handleNodeClick = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId)
    if (node) {
      setSelectedNode(node)
    }
  }

  const handleNodeDoubleClick = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId)
    if (node) {
      setEditingNode(node)
      setShowNodeEditor(true)
    }
  }

  const handleDeleteNode = async (nodeId: string) => {
    if (!graphId) return

    if (!confirm('Are you sure you want to delete this node? This will also delete all connected relationships.')) {
      return
    }

    try {
      await knowledgeGraphStore.deleteNode(graphId, nodeId)
      toast.success('Node deleted successfully')
      setSelectedNode(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete node')
    }
  }

  const handleNodeSelect = (node: GraphNode) => {
    setSelectedNode(node)
    setShowSearch(false)
  }

  if (!graphId) {
    return (
      <div className="space-y-6 p-6">
        <Card className="text-center py-12">
          <p className="text-red-600">Graph ID is required</p>
        </Card>
      </div>
    )
  }

  if (knowledgeGraphStore.isLoading && !selectedGraph) {
    return (
      <div className="space-y-6 p-6">
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading graph...</p>
        </Card>
      </div>
    )
  }

  if (knowledgeGraphStore.error) {
    return (
      <div className="space-y-6 p-6">
        <Card className="bg-red-500/10 border-red-500/20">
          <p className="text-red-600">{knowledgeGraphStore.error}</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {selectedGraph?.name}
              </h1>
              {selectedGraph?.description && (
                <p className="text-sm text-muted-foreground">{selectedGraph.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowSearch(!showSearch)}
            >
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
              Search
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowStats(!showStats)}
            >
              <ChartBarIcon className="h-5 w-5 mr-2" />
              Stats
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEditGraph}
            >
              <Cog6ToothIcon className="h-5 w-5 mr-2" />
              Settings
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Action Buttons */}
          <div className="mb-4 flex gap-2">
            <Button variant="primary" size="sm" onClick={handleCreateNode}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Node
            </Button>
            <Button variant="secondary" size="sm" onClick={handleCreateRelationship}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Relationship
            </Button>
          </div>

          {/* Search Panel */}
          {showSearch && (
            <Card className="mb-4">
              <GraphSearch graphId={graphId} onNodeSelect={handleNodeSelect} />
            </Card>
          )}

          {/* Graph Visualization */}
          <GraphVisualization
            graphId={graphId}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            height="calc(100vh - 280px)"
          />
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-card border-l border-border overflow-y-auto">
          {/* Statistics */}
          {showStats && statistics && (
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Statistics</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Total Nodes</dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {statistics.total_nodes}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Total Relationships</dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {statistics.total_relationships}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Average Degree</dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {statistics.avg_degree.toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Graph Density</dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {(statistics.density * 100).toFixed(2)}%
                  </dd>
                </div>
              </dl>

              {/* Node Type Distribution */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-foreground mb-2">Node Types</h4>
                <div className="space-y-2">
                  {Object.entries(statistics.node_type_counts).map(([type, count]) => (
                    <div key={type} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{type}</span>
                      <span className="font-medium text-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Relationship Type Distribution */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-foreground mb-2">Relationship Types</h4>
                <div className="space-y-2">
                  {Object.entries(statistics.relationship_type_counts).map(([type, count]) => (
                    <div key={type} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{type}</span>
                      <span className="font-medium text-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Selected Node Details */}
          {selectedNode && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Node Details</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingNode(selectedNode)
                      setShowNodeEditor(true)
                    }}
                    className="text-primary-600 hover:text-primary-900"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteNode(selectedNode.id)}
                    className="text-red-600 hover:text-red-600"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Badge variant="primary">{selectedNode.node_type}</Badge>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Label</h4>
                  <p className="mt-1 text-base text-foreground">{selectedNode.label}</p>
                </div>

                {selectedNode.properties && Object.keys(selectedNode.properties).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Properties</h4>
                    <dl className="space-y-2">
                      {Object.entries(selectedNode.properties).map(([key, value]) => (
                        <div key={key} className="bg-muted/50 px-3 py-2 rounded">
                          <dt className="text-xs font-medium text-muted-foreground">{key}</dt>
                          <dd className="mt-1 text-sm text-foreground">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Metadata</h4>
                    <dl className="space-y-2">
                      {Object.entries(selectedNode.metadata).map(([key, value]) => (
                        <div key={key} className="text-xs">
                          <dt className="text-muted-foreground">{key}</dt>
                          <dd className="text-foreground">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Graph Info */}
          {!selectedNode && !showStats && selectedGraph && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Graph Information</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Type</dt>
                  <dd className="mt-1">
                    <Badge variant="primary">{selectedGraph.graph_type}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                  <dd className="mt-1">
                    <Badge variant="success">{selectedGraph.status}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Nodes</dt>
                  <dd className="mt-1 text-sm text-foreground">{selectedGraph.node_count}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Edges</dt>
                  <dd className="mt-1 text-sm text-foreground">{selectedGraph.edge_count}</dd>
                </div>
                {selectedGraph.tags && selectedGraph.tags.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground mb-2">Tags</dt>
                    <dd className="flex flex-wrap gap-1">
                      {selectedGraph.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {formatDistanceToNow(new Date(selectedGraph.updated_at), { addSuffix: true })}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showNodeEditor && (
        <NodeEditor
          graphId={graphId}
          node={editingNode || undefined}
          onClose={() => {
            setShowNodeEditor(false)
            setEditingNode(null)
          }}
          onSave={(node) => {
            setSelectedNode(node)
          }}
        />
      )}

      {showRelationshipEditor && (
        <RelationshipEditor
          graphId={graphId}
          onClose={() => setShowRelationshipEditor(false)}
          onSave={() => {
            // Refresh visualization
            knowledgeGraphStore.fetchVisualization(graphId, {
              depth: 2,
              limit: 100,
            })
          }}
        />
      )}
    </div>
  )
})

GraphViewerPage.displayName = 'GraphViewerPage'

export default GraphViewerPage
